import React, { useState, useEffect } from "react";
import { Button, Row, Col, Typography, Space, Tooltip, Modal, List, Tabs, Tag } from "antd";
import {
	EyeOutlined, FileTextOutlined, ExperimentOutlined, FormOutlined, BellOutlined,
	FolderOutlined, CalendarOutlined, PlusOutlined, ReloadOutlined, LinkOutlined,
	FilePdfOutlined, AudioOutlined, UserAddOutlined,
	ShopOutlined, FileOutlined, DownloadOutlined,
} from "@ant-design/icons";
import { format } from "date-fns";

import { Patient } from "../../../../types/healthcare/PatientTypes";
import { SentNotification } from "../../../../types/communications/NotificationTypes";
import { Test } from "../../../../types/healthcare/TestTypes";
import { getNotificationType } from "../../../../api/communications/sentNotificationsApi";
import { AssignedForm } from "../../../../api/formApi";
import { ReportFile } from "../../../../types/file/FileTypes";
import { fetchSalesOrders } from "../../../../api/inventory/salesOrderApi";
import { SalesOrder } from "../../../../types/inventory/OrderTypes";

import SectionCard from "../../../../components/shared/SectionCard";
import InlineLoading from "../../../../components/shared/InlineLoading";
import StatusTag from "../../../../components/shared/StatusTag";
import EmptyState from "../../../../components/shared/EmptyState";

import CreateTestModal from "../../results/CreateTestOrderModal";
import ReportViewer from "../../../patient/components/ReportViewer";
import AssignPractitionerToSampleModal from "./submodals/AssignPractitionerToSampleModal";
import ReviewFormModal from "../../formsInTray/ReviewFormModal";
import NotificationDetailModal from "../../communications/NotificationDetailModal";
import ResourceLinkModal from "./submodals/ResourceLinkModal";
import AssignFormModal from "./submodals/AssignFormModal";
import SendManualNotification from "./submodals/SendManualNotification";
import { downloadReport, downloadFile } from "../../../../api/file/fileServiceApi";
import { PatientData } from "../PatientModal";
import { isFormDone } from "../../../../utils/forms/formStatus";
import { isGeneticAnalysisTest } from "../../../../utils/healthcare/geneticTest";
import { Company } from "../../../../types/admin/CompanyTypes";
import PatientAnalysisSection from "./PatientAnalysisSection";
import PeptidePlanBanner from "./PeptidePlanBanner";
import { hasFeature } from "../../../../utils/subscriptions/subscriptionService";
import { getTestDisplayLabel, getTestDisplayDate, hasAbnormalResults } from "../../../../utils/healthcare/testDisplayUtils";

const { Title, Text, Paragraph } = Typography;

const getChannelTag = (record: SentNotification) => {
	const type = getNotificationType(record);
	if (type === "both") {
		return (
			<Space size={4}>
				<StatusTag status="email" />
				<StatusTag status="sms" />
			</Space>
		);
	}
	if (type === "email" || type === "sms" || type === "push") return <StatusTag status={type} />;
	return <StatusTag status="unknown" label="Unknown" />;
};

interface MedicalDashboardProps {
	patient: Patient;
	patientData: PatientData;
	onRefresh: () => Promise<void>;
	isLoading: boolean;
	isLoadingReports: boolean;
	onNavigateToTab: (tab: string) => void;
	company: Company;
	analysisStatus: string | null;
}

const MedicalDashboard: React.FC<MedicalDashboardProps> = ({ patient, patientData, onRefresh, isLoading, isLoadingReports, onNavigateToTab, company, analysisStatus }) => {
	const isPeptideOnly = hasFeature("healthcare.peptide") && !hasFeature("healthcare");

	const [selectedTest, setSelectedTest] = useState<Partial<Test> | null>(null);
	const [testModalVisible, setTestModalVisible] = useState(false);
	const [selectedForm, setSelectedForm] = useState<AssignedForm | null>(null);
	const [formModalVisible, setFormModalVisible] = useState(false);
	const [selectedNotification, setSelectedNotification] = useState<SentNotification | null>(null);
	const [notificationModalVisible, setNotificationModalVisible] = useState(false);
	const [resourceModalVisible, setResourceModalVisible] = useState(false);
	const [assignFormModalVisible, setAssignFormModalVisible] = useState(false);
	const [sendNotificationModalVisible, setSendNotificationModalVisible] = useState(false);
	const [assignPractitionerModalVisible, setAssignPractitionerModalVisible] = useState(false);
	const [selectedSampleId, setSelectedSampleId] = useState<string>("");
	const [selectedReport, setSelectedReport] = useState<ReportFile | null>(null);
	const [showReportViewer, setShowReportViewer] = useState(false);

	const [recentOrders, setRecentOrders] = useState<SalesOrder[]>([]);
	const [ordersLoading, setOrdersLoading] = useState(false);
	const [viewMode, setViewMode] = useState<'cards' | 'analysis'>('cards');
	const [resourceActiveTab, setResourceActiveTab] = useState<"links" | "files">("links");

	// When a completed analysis exists, open the analysis view by default. Only
	// fires when the status flips to completed, so a manual toggle back to cards
	// is never overridden.
	useEffect(() => {
		if (analysisStatus === 'completed') {
			setViewMode('analysis');
		}
	}, [analysisStatus]);

	useEffect(() => {
		if (!patient.id || isPeptideOnly) return;
		setOrdersLoading(true);
		fetchSalesOrders({
			assignee_type: "user",
			assignee_id: patient.id,
			per_page: 5,
			sort_field: "sales_order_id",
			sort_direction: "desc",
		})
			.then((res) => setRecentOrders(res.data))
			.catch(() => {})
			.finally(() => setOrdersLoading(false));
	}, [patient.id]);

	const openAssignPractitionerModal = (sampleId: string) => {
		setSelectedSampleId(sampleId);
		setAssignPractitionerModalVisible(true);
	};

	const openTestModal = (test?: Test) => {
		if (test) {
			setSelectedTest({ ...test, patient });
		} else {
			setSelectedTest({
				patient_id: patient.id,
				priority: "routine",
				status: "ordered",
				details: {},
			});
		}
		setTestModalVisible(true);
	};

	const openFormModal = (form: AssignedForm) => {
		setSelectedForm({ ...form, patient: patient });
		setFormModalVisible(true);
	};

	const openNotificationModal = (notification: SentNotification) => {
		setSelectedNotification(notification);
		setNotificationModalVisible(true);
	};

	const openResourceModal = () => setResourceModalVisible(true);
	const openAssignFormModal = () => setAssignFormModalVisible(true);
	const openSendNotificationModal = () => setSendNotificationModalVisible(true);

	const handleReportView = (report: ReportFile) => {
		setSelectedReport(report);
		setShowReportViewer(true);
	};

	const handleReportDownload = async (report: ReportFile) => {
		try {
			const result = await downloadReport(patient.id, report.id);
			console.log(`${result.fileName} downloaded successfully`);
		} catch (error) {
			console.error("Failed to download report:", error);
		}
	};

	const recentTests = (patientData.tests ?? []).slice(0, 5);
	const recentForms = (patientData.forms ?? []).slice(0, 5);
	const recentNotifications = (Array.isArray(patientData.notifications) ? patientData.notifications : []).slice(0, 5);
	const upcomingAppointments = (patientData.appointments ?? [])
		.filter((apt) => new Date(apt.start_time as Date) > new Date())
		.sort((a, b) => new Date(a.start_time as Date).getTime() - new Date(b.start_time as Date).getTime())
		.slice(0, 5);
	const recentResources = (patientData.resourceLinks ?? []).slice(0, 5);
	const recentFiles = (patientData.files ?? []).filter((f) => !f.is_folder).slice(0, 5);

	return (
		<div>
			<Space direction="vertical" size="small" style={{ width: "100%" }}>

				{/* Peptide plan banner — only renders when active/paused plans exist */}
				<PeptidePlanBanner
					plans={patientData.treatmentPlans ?? []}
					onNavigateToTab={onNavigateToTab}
				/>

				{/* Dashboard header with view toggle */}
				<div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
					<Button
						size="small"
						onClick={() => setViewMode((v) => v === 'analysis' ? 'cards' : 'analysis')}
					>
						{viewMode === 'analysis' ? 'View Info Cards' : 'View Analysis'}
					</Button>
				</div>

				{/* Analysis view */}
				{viewMode === 'analysis' && (
					<PatientAnalysisSection
						patientId={patient.id}
						company={company?.name ?? ""}
						// `sex` is typed string and is '' on defaultPatient, so `??`
						// never falls through to `gender` — use `||` to prefer a
						// populated `gender` when `sex` is blank.
						gender={patient.sex || patient.gender}
						initialStatus={analysisStatus}
					/>
				)}

				{/* Cards view */}
				{viewMode === 'cards' && <>

				<Row gutter={[6, 6]}>
					{/* Recent Tests */}
					<Col xs={24} md={12}>
						<SectionCard
							title={<Space><ExperimentOutlined style={{ fontSize: 12 }} /><Text style={{ fontSize: 13, fontWeight: 600 }}>Recent Tests</Text></Space>}
							extra={<Button size="small" type="text" icon={<PlusOutlined />} onClick={() => openTestModal()}>New</Button>}
							style={{ height: "100%" }}
						>
							{isLoading ? (
								<InlineLoading />
							) : recentTests.length === 0 ? (
								<EmptyState description="No tests" />
							) : (
								<List size="small" dataSource={recentTests} renderItem={(test) => {
									const { date, label } = getTestDisplayDate(test);
									const isAbnormal = hasAbnormalResults(test);
									return (
										<List.Item
											style={{ padding: "4px 0", cursor: "pointer" }}
											onClick={() => openTestModal(test)}
											extra={
												<Space>
													{test.sample_id && isGeneticAnalysisTest(test) && (
														<Button type="text" size="small" icon={<UserAddOutlined />}
															onClick={(e) => { e.stopPropagation(); openAssignPractitionerModal(test.sample_id); }}
															title="Assign to Practitioner"
														/>
													)}
													<Button type="text" size="small" icon={<EyeOutlined />} />
												</Space>
											}
										>
											<List.Item.Meta
												title={<Text style={{ fontSize: 12 }}>{getTestDisplayLabel(test)}</Text>}
												description={
													<Space size={4} wrap>
														<Text type="secondary" style={{ fontSize: 11 }}>{label} {date}</Text>
														<StatusTag status={test.status} size="small" />
														{isAbnormal && (
															<Tag style={{ fontSize: 10, padding: "0 4px", margin: 0, background: "var(--mg-danger-bg)", borderColor: "var(--mg-danger)", color: "var(--mg-danger)" }}>
																Abnormal
															</Tag>
														)}
													</Space>
												}
											/>
										</List.Item>
									);
								}} />
							)}
						</SectionCard>
					</Col>

					{/* Recent Forms */}
					<Col xs={24} md={12}>
						<SectionCard
							title={<Space><FormOutlined style={{ fontSize: 12 }} /><Text style={{ fontSize: 13, fontWeight: 600 }}>Recent Forms</Text></Space>}
							extra={<Button size="small" type="text" icon={<PlusOutlined />} onClick={() => openAssignFormModal()}>New</Button>}
							style={{ height: "100%" }}
						>
							{isLoading ? (
								<InlineLoading />
							) : recentForms.length === 0 ? (
								<EmptyState description="No forms" />
							) : (
								<List size="small" dataSource={recentForms} renderItem={(form) => (
									<List.Item
										style={{ padding: "4px 0", cursor: "pointer" }}
										onClick={() => isFormDone(form.status) && openFormModal(form)}
										extra={isFormDone(form.status) && <Button type="text" size="small" icon={<EyeOutlined />} />}
									>
										<List.Item.Meta
											title={<Text style={{ fontSize: 12 }}>{form.form?.title || "Form"}</Text>}
											description={
												<Space size={4}>
													<Text type="secondary" style={{ fontSize: 11 }}>{form.created_at ? format(new Date(form.created_at), "MMM dd, yyyy") : ""}</Text>
													<StatusTag status={form.status} size="small" />
												</Space>
											}
										/>
									</List.Item>
								)} />
							)}
						</SectionCard>
					</Col>

					{/* Upcoming Appointments — hidden for peptide-only */}
					{!isPeptideOnly && (
						<Col xs={24} md={12}>
							<SectionCard
								title={<Space><CalendarOutlined style={{ fontSize: 12 }} /><Text style={{ fontSize: 13, fontWeight: 600 }}>Upcoming Appointments</Text></Space>}
								style={{ height: "100%" }}
							>
								{isLoading ? (
									<InlineLoading />
								) : upcomingAppointments.length === 0 ? (
									<EmptyState description="No upcoming appointments" />
								) : (
									<List size="small" dataSource={upcomingAppointments} renderItem={(apt) => (
										<List.Item style={{ padding: "4px 0" }}>
											<List.Item.Meta
												title={<Text style={{ fontSize: 12 }}>{apt.title || "Appointment"}</Text>}
												description={
													<Space size={4} direction="vertical" style={{ width: "100%" }}>
														<Text type="secondary" style={{ fontSize: 11 }}>{apt.start_time ? format(new Date(apt.start_time as Date), "MMM dd, yyyy hh:mm a") : ""}</Text>
														<Space size={4}>
															<StatusTag status={apt.status} size="small" />
															{apt.location && <Text type="secondary" style={{ fontSize: 10 }}>{apt.location}</Text>}
														</Space>
													</Space>
												}
											/>
										</List.Item>
									)} />
								)}
							</SectionCard>
						</Col>
					)}

					{/* Recent Notifications — hidden for peptide-only */}
					{!isPeptideOnly && (
						<Col xs={24} md={12}>
							<SectionCard
								title={<Space><BellOutlined style={{ fontSize: 12 }} /><Text style={{ fontSize: 13, fontWeight: 600 }}>Recent Notifications</Text></Space>}
								extra={<Button size="small" type="text" icon={<PlusOutlined />} onClick={() => openSendNotificationModal()}>Send</Button>}
								style={{ height: "100%" }}
							>
								{isLoading ? (
									<InlineLoading />
								) : recentNotifications.length === 0 ? (
									<EmptyState description="No notifications" />
								) : (
									<List size="small" dataSource={recentNotifications} renderItem={(notif) => (
										<List.Item
											style={{ padding: "4px 0", cursor: "pointer" }}
											onClick={() => openNotificationModal(notif)}
											extra={<Space size={4}>{getChannelTag(notif)}<Button type="text" size="small" icon={<EyeOutlined />} /></Space>}
										>
											<List.Item.Meta
												title={<Text strong style={{ fontSize: 12 }}>{notif.template_name || "Notification"}</Text>}
												description={
													<Space size={4} direction="vertical" style={{ width: "100%" }}>
														{notif.subject && <Text type="secondary" style={{ fontSize: 11 }}>{notif.subject}</Text>}
														<Text type="secondary" style={{ fontSize: 11 }}>{notif.created_at ? format(new Date(notif.created_at), "MMM dd, yyyy hh:mm a") : ""}</Text>
													</Space>
												}
											/>
										</List.Item>
									)} />
								)}
							</SectionCard>
						</Col>
					)}

					{/* Test Reports — uses isLoadingReports for its own skeleton */}
					<Col xs={24} md={12}>
						<SectionCard
							title={<Space><FilePdfOutlined style={{ fontSize: 12 }} /><Text style={{ fontSize: 13, fontWeight: 600 }}>Test Reports</Text></Space>}
							style={{ height: "100%" }}
						>
							{isLoadingReports ? (
								<InlineLoading />
							) : patientData.reports.length === 0 ? (
								<EmptyState description="No reports available" />
							) : (
								<List size="small" dataSource={patientData.reports.slice(0, 5)} renderItem={(report) => {
									const isAudio = report.file_name.toLowerCase().includes("audio");
									const isPdf = report.file_type === "application/pdf" || report.file_name.toLowerCase().endsWith(".pdf");
									return (
										<List.Item
											style={{ padding: "4px 0", cursor: isPdf ? "pointer" : "default" }}
											onClick={() => isPdf && handleReportView(report)}
											extra={
												<Button type="text" size="small"
													icon={isPdf ? <EyeOutlined /> : isAudio ? <AudioOutlined /> : <FileTextOutlined />}
													onClick={(e) => { e.stopPropagation(); isPdf ? handleReportView(report) : handleReportDownload(report); }}
												>
													{isPdf ? "View" : "Download"}
												</Button>
											}
										>
											<List.Item.Meta
												title={<Text style={{ fontSize: 12 }}>{report.file_name}</Text>}
												description={
													<Space size={4}>
														<Text type="secondary" style={{ fontSize: 11 }}>{report.created_at ? format(new Date(report.created_at), "MMM dd, yyyy") : ""}</Text>
\1{isAudio && <StatusTag status="audio" size="small" />}
														{isPdf && <Tag color="red" style={{ fontSize: 10, padding: "0 4px", margin: 0 }}>PDF</Tag>}
													</Space>
												}
											/>
										</List.Item>
									);
								}} />
							)}
						</SectionCard>
					</Col>

					{/* Resources */}
					<Col xs={24} md={12}>
						<SectionCard
							title={<Space><FolderOutlined style={{ fontSize: 12 }} /><Text style={{ fontSize: 13, fontWeight: 600 }}>Resources</Text></Space>}
							extra={
								resourceActiveTab === "links" ? (
									<Button size="small" type="text" icon={<PlusOutlined />} onClick={openResourceModal}>Add</Button>
								) : null
							}
							style={{ height: "100%" }}
							noPadding
						>
							<Tabs
								size="small"
								activeKey={resourceActiveTab}
								onChange={(key) => setResourceActiveTab(key as "links" | "files")}
								style={{ padding: "0 12px" }}
								items={[
									{
										key: "links",
										label: <Space size={4}><LinkOutlined />Links</Space>,
										children: isLoading ? (
											<InlineLoading />
										) : recentResources.length === 0 ? (
											<EmptyState description="No resource links" />
										) : (
											<List size="small" dataSource={recentResources} renderItem={(resource) => (
												<List.Item
													style={{ padding: "4px 0" }}
													extra={<Button type="text" size="small" icon={<LinkOutlined />} onClick={() => window.open(resource.link_url, "_blank")} />}
												>
													<List.Item.Meta
														title={<Text style={{ fontSize: 12 }}>{resource.resource_type}</Text>}
														description={<Paragraph ellipsis={{ rows: 1 }} style={{ fontSize: 11, margin: 0, color: "var(--mg-text-secondary)" }}>{resource.description}</Paragraph>}
													/>
												</List.Item>
											)} />
										),
									},
									{
										key: "files",
										label: <Space size={4}><FileOutlined />Files</Space>,
										children: isLoading ? (
											<InlineLoading />
										) : recentFiles.length === 0 ? (
											<EmptyState description="No files" />
										) : (
											<List size="small" dataSource={recentFiles} renderItem={(file) => (
												<List.Item
													style={{ padding: "4px 0" }}
													extra={
														<Button
															type="text" size="small" icon={<DownloadOutlined />}
															onClick={() => downloadFile(patient.id, file.id)}
														/>
													}
												>
													<List.Item.Meta
														title={<Text style={{ fontSize: 12 }}>{file.file_name}</Text>}
														description={
															<Space size={4}>
																<Text type="secondary" style={{ fontSize: 11 }}>
																	{file.created_at ? format(new Date(file.created_at), "MMM dd, yyyy") : ""}
																</Text>
																{file.file_size > 0 && (
																	<Text type="secondary" style={{ fontSize: 11 }}>
																		{file.file_size < 1024 * 1024
																			? `${(file.file_size / 1024).toFixed(1)} KB`
																			: `${(file.file_size / (1024 * 1024)).toFixed(1)} MB`}
																	</Text>
																)}
															</Space>
														}
													/>
												</List.Item>
											)} />
										),
									},
								]}
							/>
						</SectionCard>
					</Col>

					{/* Recent Orders — hidden for peptide-only */}
					{!isPeptideOnly && (
						<Col xs={24}>
							<SectionCard
								title={
									<Space>
										<ShopOutlined style={{ fontSize: 12 }} />
										<Text style={{ fontSize: 13, fontWeight: 600 }}>Recent Orders</Text>
										{patient.shopify_customer_id && (
											<StatusTag status="active" label="Shopify" size="small" />
										)}
									</Space>
								}
							>
								{ordersLoading ? (
									<InlineLoading />
								) : recentOrders.length === 0 ? (
									<EmptyState description="No orders" />
								) : (
									<List
										size="small"
										dataSource={recentOrders}
										renderItem={(order) => (
											<List.Item
												style={{ padding: "4px 0" }}
												extra={
													<Space size={4}>
														{order.shopify_financial_status && (
															<StatusTag status={order.shopify_financial_status} size="small" />
														)}
														<StatusTag status={order.status ?? ""} size="small" />
														<Text strong style={{ fontSize: 12 }}>${Number(order.total_amount ?? 0).toFixed(2)}</Text>
													</Space>
												}
											>
												<List.Item.Meta
													title={
														<Space size={6}>
															{order.is_shopify_order && <ShopOutlined style={{ color: "#96bf48", fontSize: 11 }} />}
															<Text style={{ fontSize: 12 }}>
																{order.shopify_order_identifier ?? `Order #${order.sales_order_id}`}
															</Text>
														</Space>
													}
													description={
														<Space size={4}>
															<Text type="secondary" style={{ fontSize: 11 }}>
																{order.order_date ? format(new Date(order.order_date), "MMM dd, yyyy") : ""}
															</Text>
															{(order.items?.length ?? 0) > 0 && (
																<Text type="secondary" style={{ fontSize: 11 }}>
																	· {order.items!.length} item{order.items!.length !== 1 ? "s" : ""}
																</Text>
															)}
														</Space>
													}
												/>
											</List.Item>
										)}
									/>
								)}
							</SectionCard>
						</Col>
					)}
				</Row>

				</> /* end cards view */}
			</Space>

			{testModalVisible && selectedTest && (
				<CreateTestModal
					isOpen={testModalVisible}
					onClose={() => { setTestModalVisible(false); setSelectedTest(null); onRefresh(); }}
					onSave={async () => { setTestModalVisible(false); await onRefresh(); }}
					currentOrder={selectedTest}
					setCurrentOrder={setSelectedTest}
					patient={patient}
					allTests={patientData.tests}
				/>
			)}

			{assignPractitionerModalVisible && selectedSampleId && (
				<AssignPractitionerToSampleModal
					visible={assignPractitionerModalVisible}
					onClose={() => { setAssignPractitionerModalVisible(false); setSelectedSampleId(""); }}
					patient={patient}
					sampleId={selectedSampleId}
				/>
			)}

			{formModalVisible && selectedForm && (
				<ReviewFormModal
					isOpen={formModalVisible}
					onClose={() => { setFormModalVisible(false); setSelectedForm(null); }}
					onSave={() => { setFormModalVisible(false); setSelectedForm(null); }}
					assignedForm={selectedForm}
				/>
			)}

			{notificationModalVisible && selectedNotification && (
				<NotificationDetailModal
					notification={selectedNotification}
					isOpen={notificationModalVisible}
					onClose={() => { setNotificationModalVisible(false); setSelectedNotification(null); }}
				/>
			)}

			{resourceModalVisible && (
				<ResourceLinkModal
					visible={resourceModalVisible}
					onClose={() => { setResourceModalVisible(false); onRefresh(); }}
					onResourceLinkSaved={async () => { setResourceModalVisible(false); await onRefresh(); }}
					patientId={patient.id}
					companyId={company.id}
					editingLink={null}
				/>
			)}

			{assignFormModalVisible && (
				<AssignFormModal
					isOpen={assignFormModalVisible}
					onClose={() => { setAssignFormModalVisible(false); onRefresh(); }}
					patient={patient}
					company={company}
				/>
			)}

			{sendNotificationModalVisible && (
				<SendManualNotification
					isOpen={sendNotificationModalVisible}
					onClose={() => { setSendNotificationModalVisible(false); onRefresh(); }}
					recipient={patient}
				/>
			)}

			{showReportViewer && selectedReport && (
				<ReportViewer
					visible={showReportViewer}
					onClose={() => { setShowReportViewer(false); setSelectedReport(null); }}
					report={selectedReport}
					patientId={patient.id}
				/>
			)}
		</div>
	);
};

export default MedicalDashboard;
